import { makeAutoObservable, runInAction } from 'mobx';
import {
  ListingFormat,
  ListingType,
} from '@Encore-FastStartup-Phase-Out/encore-shared';

import { ListingApi } from '../api/ListingApi';
import {
  ActiveListing,
  FlashListingsEvent,
  ListingQueryDto,
  SoldListing,
} from '../constants/types';
import { toLocal } from '../utils/time';

export class ListingStore {
  api: ListingApi;
  activeListings: ActiveListing[] = [];

  flashActiveListings: ActiveListing[] = [];
  flashListingsEvents: FlashListingsEvent[] = [];
  currentFlashEvent: FlashListingsEvent | null = null;
  enablePosting: boolean = false;
  enablePurchasing: boolean = false;

  soldListings: SoldListing[] = [];
  totalActiveListings: number = 0;
  totalSoldListings: number = 0;
  displayedActiveListing: ActiveListing = {
    id: '',
    title: '',
    description: '',
    listingType: ListingType.DEFAULT,
    video: '',
    createdAt: '',
    inStock: 0,
    user: { id: '', userName: '', email: '' },
    bid: 0,
    startingBid: 0,
    massUnit: 'oz',
    weight: 0,
    width: 0,
    height: 0,
    length: 0,
    featured: false,
    maxBids: [],
    format: ListingFormat.CLASSIC,
    categoryId: '',
    subCategoryId: '',
  };
  displayedSoldListing: SoldListing = {
    id: '',
    listing: {
      id: '',
      title: '',
      description: '',
      listingType: ListingType.DEFAULT,
      video: '',
      createdAt: '',
    },
    seller: { id: '', userName: '', email: '' },
    user: { id: '', userName: '', email: '' },
    createdAt: '',
    price: 0,
    shippingPrice: 0,
    status: '',
    trackingCode: '',
  };
  soldListingsSearch: string = '';
  activeListingsSearch: string = '';
  loading: boolean = false;
  loadingPostingStatus: boolean = false;
  loadingPurchasingStatus: boolean = false;
  flashSaleStartTime: string = '';
  flashSaleEndTime: string = '';

  constructor() {
    this.api = new ListingApi();
    makeAutoObservable(this, undefined, { autoBind: true });
  }

  setFlashSaleWindow(start: string, end: string) {
    this.flashSaleStartTime = start;
    this.flashSaleEndTime = end;
  }

  setActiveListings(listings: ActiveListing[]) {
    this.activeListings = listings;
  }

  setDisplayedActiveListing(listing: ActiveListing) {
    this.displayedActiveListing = listing;
  }

  setDisplayedSoldListing(listing: SoldListing) {
    this.displayedSoldListing = listing;
  }

  setSoldListings(listings: SoldListing[]) {
    this.soldListings = listings;
  }

  setActiveListingsSearch(value: string) {
    this.activeListingsSearch = value;
  }

  setSoldListingsSearch(value: string) {
    this.soldListingsSearch = value;
  }

  setTotalActiveListings(value: number) {
    this.totalActiveListings = value;
  }

  setTotalSoldListings(value: number) {
    this.totalSoldListings = value;
  }

  setLoading(loading: boolean) {
    this.loading = loading;
  }

  setCurrentFlashEvent(event: FlashListingsEvent | null) {
    this.currentFlashEvent = event;
  }

  setEnablePosting(value: boolean) {
    this.enablePosting = value;
  }

  setEnablePurchasing(value: boolean) {
    this.enablePurchasing = value;
  }

  setLoadingPostingStatus(loading: boolean) {
    this.loadingPostingStatus = loading;
  }

  setLoadingPurchasingStatus(loading: boolean) {
    this.loadingPurchasingStatus = loading;
  }

  async fetchActiveListings(query?: Partial<ListingQueryDto>) {
    query = {
      ...(this.activeListingsSearch && { search: this.activeListingsSearch }),
      ...(query || {}),
    };

    this.setLoading(true);
    const res = await this.api.getActiveListings(query);
    this.setTotalActiveListings(res.count);

    if (query?.page === 1) {
      this.setActiveListings(res.items);
    } else {
      this.setActiveListings([...this.activeListings, ...res.items]);
    }
    this.setLoading(false);
  }

  async fetchEnableBinPosting() {
    this.setLoadingPostingStatus(true);
    try {
      const result = await this.api.getEnableBinPosting();
      runInAction(() => {
        this.setEnablePosting(result);
      });
    } catch (error) {
      console.error('Error fetching enable BIN posting:', error);
    } finally {
      this.setLoadingPostingStatus(false);
    }
  }

  async fetchEnablePurchasing() {
    this.setLoadingPurchasingStatus(true);
    try {
      const result = await this.api.getEnablePurchasing();
      runInAction(() => {
        this.setEnablePurchasing(result);
      });
    } catch (error) {
      console.error('Error fetching enable purchasing:', error);
    } finally {
      this.setLoadingPurchasingStatus(false);
    }
  }

  async updateEnablePosting(enabled: boolean) {
    this.setLoadingPostingStatus(true);
    try {
      await this.api.updateEnablePosting(enabled);
      runInAction(() => {
        this.setEnablePosting(enabled);
      });
    } catch (error) {
      console.error('Error updating enable posting:', error);
      throw error;
    } finally {
      this.setLoadingPostingStatus(false);
    }
  }

  async updateEnablePurchasing(enabled: boolean) {
    this.setLoadingPurchasingStatus(true);
    try {
      await this.api.updateEnablePurchasing(enabled);
      runInAction(() => {
        this.setEnablePurchasing(enabled);
      });
    } catch (error) {
      console.error('Error updating enable purchasing:', error);
      throw error;
    } finally {
      this.setLoadingPurchasingStatus(false);
    }
  }

  async fetchSoldListings(query?: Partial<ListingQueryDto>) {
    query = {
      ...(this.soldListingsSearch && { search: this.soldListingsSearch }),
      ...(query || {}),
    };
    this.setLoading(true);

    const res = await this.api.getSoldListings(query);
    this.setTotalSoldListings(res.count);

    if (query?.page === 1) {
      this.setSoldListings(res.items);
    } else {
      this.setSoldListings([...this.soldListings, ...res.items]);
    }
    this.setLoading(false);
  }

  async deleteOrder(id: string) {
    await this.api.deleteOrder(id);
  }

  async deleteListing(id: string) {
    await this.api.deleteListing(id);
  }

  async deleteAllActiveUserListings(userId: string) {
    await this.api.deleteAllActiveListingsForUser(userId);
  }

  async updateListingInStore(listingId: string) {
    const updatedListing = await this.api.getActiveListingById(listingId);
    this.setDisplayedActiveListing(updatedListing);
    const index = this.activeListings.findIndex((l) => l.id === listingId);

    if (index !== -1) {
      this.setActiveListings([
        ...this.activeListings.slice(0, index),
        updatedListing,
        ...this.activeListings.slice(index + 1),
      ]);
    }
  }

  async updateSoldListingInStore(listingId: string) {
    const updatedListing = await this.api.getSoldListingById(listingId);
    this.setDisplayedSoldListing(updatedListing);
    const index = this.soldListings.findIndex((l) => l.id === listingId);

    if (index !== -1) {
      this.setSoldListings([
        ...this.soldListings.slice(0, index),
        updatedListing,
        ...this.soldListings.slice(index + 1),
      ]);
    }
  }

  async updateListing(listing: ActiveListing) {
    await this.api.updateActiveListing(listing.id, listing);
    await this.updateListingInStore(listing.id);
  }

  async cancelOrder(id: string) {
    await this.api.cancelOrder(id);
    await this.updateSoldListingInStore(id);
  }

  async featureListing(id: string, featured: boolean) {
    const { featured: featuredResult } = await this.api.featureListing(
      id,
      featured,
    );

    runInAction(() => {
      const activeListingIndex = this.activeListings.findIndex(
        (listing) => listing.id === id,
      );
      if (activeListingIndex !== -1) {
        this.activeListings[activeListingIndex].featured = featuredResult;
      }
    });
  }

  async updateFlashSaleWindow(startTime: string, endTime: string) {
    this.setLoading(true);
    try {
      await this.api.updateFlashSaleWindow(startTime, endTime);
    } catch (error) {
      console.error('Error updating flash sale window:', error);
    } finally {
      this.setLoading(false);
    }
  }

  async fetchFlashSaleWindow() {
    this.setLoading(true);
    try {
      const { startTime, endTime } = await this.api.getFlashSaleWindow();

      const localStart = toLocal(startTime);
      const localEnd = toLocal(endTime);
      runInAction(() => this.setFlashSaleWindow(localStart, localEnd));
    } catch (error) {
      console.error('Error fetching flash sale window:', error);
    } finally {
      this.setLoading(false);
    }
  }

  async getListingEventGroups() {
    this.setLoading(true);
    try {
      const events = await this.api.getListingEventGroups();
      runInAction(() => {
        this.flashListingsEvents = events;
      });
    } catch (error) {
      console.error('Error fetching listing event groups:', error);
    } finally {
      this.setLoading(false);
    }
  }

  async getEventListings(bucketTime: number, groupId: string) {
    this.setLoading(true);
    try {
      const { items } = await this.api.getEventListings(bucketTime, groupId);

      runInAction(() => {
        this.flashActiveListings = items;
      });
    } catch (error) {
      console.error('Error fetching event listings:', error);
    } finally {
      this.setLoading(false);
    }
  }

  // To be implemented
  setFlashActiveListingsOrder(reorderedListings: ActiveListing[]) {
    runInAction(() => {
      this.flashActiveListings = reorderedListings;
      // Also update the main activeListings array to maintain consistency
      const flashListingIds = new Set(reorderedListings.map((l) => l.id));
      const nonFlashListings = this.activeListings.filter(
        (listing) => !flashListingIds.has(listing.id),
      );
      this.activeListings = [...nonFlashListings, ...reorderedListings];
    });
  }

  updateFlashListingSchedule(reorderedListings: ActiveListing[]) {
    this.setFlashActiveListingsOrder(reorderedListings);
  }

  async bulkUpdateEventListings() {
    this.setLoading(true);
    try {
      await this.api.bulkUpdate(
        this.flashActiveListings.map((listing) => {
          return {
            id: listing.id,
            startTime: Number(listing.startTime),
            endTime: Number(listing.endTime),
            listingType: listing.listingType,
            marketPrice: listing.marketPrice,
            snipePrice: listing.snipePrice ?? null,
          };
        }),
      );
    } catch (error) {
      console.error('Error bulk updating event listings:', error);
    } finally {
      this.setLoading(false);
    }
  }

  async getEbayCrossListStatus(id: string) {
    return this.api.getEbayCrossListStatus(id);
  }

  async crossListToEbay(id: string, price?: number, quantity?: number) {
    return this.api.crossListToEbay(
      id,
      price ?? this.displayedActiveListing.price!,
      quantity ?? this.displayedActiveListing.inStock,
    );
  }

  async retryEbayCrossList(id: string) {
    return this.api.retryEbayCrossList(id);
  }
}
